import type { AdsPickerCandidate } from '@/hooks/useAdsConnectFlow';

/**
 * Reihenfolge und Suche der Kontoauswahl (Nachtrag zum Ads-Kapitel, 12.09.2026).
 *
 * Reine Funktionen ohne React und ohne Datenzugriff: die Auswahl eines Kontos
 * ist eine Entscheidung mit Folgen (der naechtliche Pull laeuft danach gegen
 * dieses Konto), und die Regeln dahinter gehoeren an eine Stelle, an der man
 * sie einzeln pruefen kann.
 *
 * Anlass: ein MCC-Grant (google@oma.ag) fuehrt rund 150 Ads-Konten. Eine
 * unsortierte Liste in der Reihenfolge der Datenbank ist dort keine Auswahl,
 * sondern eine Suchaufgabe.
 */

/**
 * Ab wann der Dialog ein Suchfeld zeigt.
 *
 * Acht Zeilen liest man; ab der neunten sucht man. Die Zahl ist eine
 * Abwaegung und keine Messung -- zu frueh eingeblendet ist das Feld ein
 * zusaetzliches Bedienelement fuer nichts.
 */
export const PICKER_SEARCH_THRESHOLD = 8;

/**
 * Rang einer Zeile. Kleiner heisst weiter oben.
 *
 * Ein VORGESCHLAGENES, aber nicht waehlbares Konto bleibt bewusst unten: Google
 * kann eine Verknuepfung auf ein Verwaltungskonto melden, und ein Vorschlag,
 * den man nicht anklicken kann, oben zu zeigen laedt zu einem Klick ein, der
 * nichts tut.
 */
function rankOf(c: AdsPickerCandidate): number {
  if (c.selectable && c.suggested) return 0;
  if (c.selectable) return 1;
  if (c.linkedElsewhere) return 2;
  return 3;
}

/** Wonach innerhalb einer Gruppe sortiert wird -- dieselbe Angabe, die die Zeile fuehrt. */
function sortKey(c: AdsPickerCandidate): string {
  return (c.name ?? c.adsCustomerId).toLocaleLowerCase();
}

/** Vorschlag vor waehlbar vor fremd verknuepft vor Verwaltungskonto, innerhalb nach Name. */
export function sortPickerCandidates(candidates: AdsPickerCandidate[]): AdsPickerCandidate[] {
  // Kopie: die Liste kommt aus einem `useMemo` des Hooks, und `sort` sortiert
  // an Ort und Stelle.
  return [...candidates].sort((a, b) => {
    const byRank = rankOf(a) - rankOf(b);
    if (byRank !== 0) return byRank;
    return sortKey(a).localeCompare(sortKey(b));
  });
}

/**
 * Ab wie vielen Ziffern die Eingabe ueberhaupt als Kundennummer gelesen wird.
 *
 * Der Zweig lief zuerst ab EINER Ziffer, und damit WEITETE die Suche, statt
 * einzugrenzen: "Konto 1" zieht die 1 heraus und trifft praktisch jede
 * zehnstellige Kundennummer -- bei einem MCC-Grant stand nach dem Tippen also
 * wieder die vollstaendige Liste da, genau das Gegenteil dessen, wofuer das
 * Feld gebaut ist. Drei ist die kuerzeste Eingabe, bei der jemand ernsthaft
 * eine Nummer meint; darunter ist eine Ziffer im Text viel wahrscheinlicher
 * als eine abgetippte Nummer.
 */
export const PICKER_DIGIT_SEARCH_MIN = 3;

/**
 * Trifft die Eingabe diese Zeile?
 *
 * Zwei Suchgrundlagen, getrennt gehalten: Name UND Kundennummer. Bindestriche
 * und Leerzeichen in der Eingabe fallen weg, weil die Nummer ueberall sonst
 * maskiert dargestellt wird (`656-711-XXXX`) und genau so abgetippt wird.
 *
 * Eine rein aus Ziffern bestehende Eingabe sucht NICHT im Namen -- sonst
 * traefe "2578" jeden Kontonamen, der zufaellig eine Zahl enthaelt, und die
 * Suche nach einer Kundennummer waere genau dann unbrauchbar, wenn man sie
 * braucht. Dieser Riegel haengt aber an der Nummernsuche selbst: lief sie gar
 * nicht (unter `PICKER_DIGIT_SEARCH_MIN` Ziffern), gibt es nichts zu
 * schuetzen, und "58" darf dann "Konto 58" finden.
 */
export function matchesPickerSearch(c: AdsPickerCandidate, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length === 0) return true;

  const digits = trimmed.replace(/\D/g, '');
  const searchesNumber = digits.length >= PICKER_DIGIT_SEARCH_MIN;
  if (searchesNumber && c.adsCustomerId.includes(digits)) return true;

  const isDigitsOnly = digits.length === trimmed.length;
  if (searchesNumber && isDigitsOnly) return false;

  return (c.name ?? '').toLocaleLowerCase().includes(trimmed.toLocaleLowerCase());
}

/**
 * Die Kundennummern aus der Antwort von `ads-link-suggest`.
 *
 * Nur ziffernreine Werte: `google_ads_accounts.ads_customer_id` ist ziffernrein,
 * alles andere traefe keine Zeile und stuende nur als stiller Nicht-Treffer in
 * der Liste. Eine unlesbare Antwort ist eine leere Liste und kein Fehler -- der
 * Vorschlag ist Komfort, das Zuordnen die Aufgabe.
 */
export function parseSuggestedCustomerIds(data: unknown): string[] {
  const ids = (data as { suggested_customer_ids?: unknown } | null)?.suggested_customer_ids;
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is string => typeof id === 'string' && /^\d+$/.test(id));
}
